// The mark, and the wordmark beside it.
//
// The mark is the robot's head from the hero illustrations: a slim indigo
// bezel with a little depth at the lower edge, a glossy dark screen with
// glowing cyan eyes and smile, and a lit amber antenna bulb. Chosen on
// 2026-09-30 over the speech bubble with a lightning bolt, which could have
// been any chat product's.
//
// Drawn inline rather than shipped as a file so it needs no extra request and
// stays sharp at every size. public/favicon.svg is the same drawing, kept in
// step with this by hand, and client/scripts/make-brand-images.py rasterizes
// that file for the PNG icons and the share image. SVG ids are document-wide,
// so every gradient and filter id is salted per instance: two marks on one
// page sharing an id is the bug that renders fine in one browser and a black
// square in another.

import { useId } from "react";

export function LogoMark({ className = "h-8 w-8" }: { className?: string }) {
  const id = useId();
  const bezel = `${id}-bezel`;
  const screen = `${id}-screen`;
  const bulb = `${id}-bulb`;
  const glow = `${id}-glow`;
  const eyes = "M11.2 23.7q2.3-3.1 4.6 0M24.2 23.7q2.3-3.1 4.6 0";
  const smile = "M17 27.7q3 2.3 6 0";

  return (
    <svg className={className} viewBox="0 0 40 40" fill="none" aria-hidden="true">
      <defs>
        <linearGradient id={bezel} x1="0" y1="8.8" x2="0" y2="39" gradientUnits="userSpaceOnUse">
          <stop stopColor="#7C7AF4" />
          <stop offset="1" stopColor="#4F46E5" />
        </linearGradient>
        <linearGradient id={screen} x1="0" y1="8.8" x2="0" y2="39" gradientUnits="userSpaceOnUse">
          <stop stopColor="#1F1D4D" />
          <stop offset="1" stopColor="#0E0C2B" />
        </linearGradient>
        <radialGradient id={bulb} cx="20" cy="3.4" r="3.4" gradientUnits="userSpaceOnUse">
          <stop stopColor="#FDE68A" />
          <stop offset="1" stopColor="#F59E0B" />
        </radialGradient>
        <filter id={glow} x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="0.7" />
        </filter>
      </defs>

      {/* The antenna: a short stem and a lit bulb with a glint. */}
      <rect x="18.9" y="4.6" width="2.2" height="4.6" rx="1.1" fill="#3730A3" />
      <circle cx="20" cy="3.4" r="3" fill={`url(#${bulb})`} />
      <circle cx="19" cy="2.5" r="0.9" fill="#FFFBEB" opacity=".9" />

      {/* The head: its shadowed lower edge, then the bezel over it. */}
      <rect x="1.2" y="9.8" width="37.6" height="30" rx="9" fill="#3730A3" />
      <rect data-part="head" x="1.2" y="8.8" width="37.6" height="29" rx="9" fill={`url(#${bezel})`} />

      {/* The screen, a reflection on the glass, and the face with its glow. */}
      <rect data-part="screen" x="3.5" y="11.1" width="33" height="24.4" rx="7" fill={`url(#${screen})`} />
      <path d="M6.5 17.1q1-4 6-4.4" stroke="#FFFFFF" strokeWidth="1.4" strokeLinecap="round" opacity=".22" />
      <g filter={`url(#${glow})`} opacity=".85">
        <path d={eyes} stroke="#67E8F9" strokeWidth="2.5" strokeLinecap="round" />
        <path d={smile} stroke="#67E8F9" strokeWidth="2" strokeLinecap="round" />
      </g>
      <path d={eyes} stroke="#67E8F9" strokeWidth="2.5" strokeLinecap="round" />
      <path d={smile} stroke="#67E8F9" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

// Mark plus name. The name is the wordmark the other Nebulon Labs products
// share (StockPicksNow, GameLinePicks, GolfSwingScore): one word, title
// cased, heavy, with the middle word in the brand colour. Screen readers get
// the name as three words. `tone` picks the colours: the marketing header
// sits on white, the footer on near-black. `compact` drops the name to screen
// readers only on the narrowest phones, where a header cannot fit it beside
// its buttons.
//
// A link around it must be a flex box (className="flex"), as every header's
// is. A plain inline link sets the logo on a text line, with room for
// descenders under it, so the link comes out 38px tall instead of 32 and a
// header that centers its row lifts the wordmark about 3px above the tabs.
export function Logo({
  className = "",
  tone = "dark",
  compact = false
}: {
  className?: string;
  tone?: "dark" | "light";
  // true: the name drops below 370px, for the dashboard header and its
  // buttons. "tight": only below 340px, for the marketing header, which has
  // just Start free and a menu beside it.
  compact?: boolean | "tight";
}) {
  return (
    <span className={`inline-flex items-center gap-2.5 ${className}`}>
      {/* Raised so the middle of the head, not of the whole drawing, lines up
          with the wordmark: the antenna takes the top 9 of the 40 units, so
          the head's middle (y 24.3) sits 3.4px below the mark's at 32px.
          A transform, so the row's height and spacing do not change. */}
      <LogoMark className="h-8 w-8 shrink-0 -translate-y-[3.4px]" />
      <span
        className={
          "whitespace-nowrap text-[17px] font-extrabold tracking-[-0.02em] " +
          (tone === "light" ? "text-white" : "text-slate-900") +
          (compact === "tight" ? " max-[339px]:sr-only" : compact ? " max-[369px]:sr-only" : "")
        }
      >
        <span className="sr-only">Build Bots Now</span>
        <span aria-hidden="true" data-testid="wordmark">
          Build<span className={tone === "light" ? "text-indigo-300" : "text-brand-600"}>Bots</span>Now
        </span>
      </span>
    </span>
  );
}
